import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { FieldMap } from 'angular2-query-builder/dist/components';
import { additionalFieldsWithStrings } from 'app/store/project/addition-fields/addition-fields.selectors';
import * as CourierSelectors from 'app/store/project/couriers/couriers.selectors';
import * as GoodSelectors from 'app/store/project/goods/goods.selectors';
import * as SenderSelectors from 'app/store/project/senders/senders.selectors';
import * as StatusSelectors from 'app/store/project/statuses/statuses.selectors';
import { defaultAdditionalFieldNumber } from 'core/utils/additional-field-label';
import { selectFirst } from 'core/utils/rx-common';
import { isEqual } from 'lodash';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, filter, first, map, startWith, switchMap } from 'rxjs/operators';

@Injectable()
export class OrderFieldMapService {
  constructor(private store$: Store, private translateService: TranslateService) {}

  getOrderFieldMap(): Observable<FieldMap> {
    const transformer = map((entities: { id: string; name: string }[]) => {
      return entities.map(e => ({ value: e.id, name: e.name }));
    });

    const queries = combineLatest([
      this.store$.select(CourierSelectors.couriersState).pipe(
        filter(s => s.loaded),
        switchMap(() => this.store$.pipe(selectFirst(CourierSelectors.couriers))),
        transformer,
      ),
      this.store$.select(StatusSelectors.statusesState).pipe(
        filter(s => s.loaded),
        switchMap(() => this.store$.pipe(selectFirst(StatusSelectors.activeStatuses))),
        transformer,
      ),
      this.store$.select(SenderSelectors.sendersState).pipe(
        filter(s => s.loaded),
        switchMap(() => this.store$.pipe(selectFirst(SenderSelectors.activatedSenders))),
        transformer,
      ),
      this.store$.select(GoodSelectors.goodsState).pipe(
        filter(s => s.loaded),
        switchMap(() => this.store$.pipe(selectFirst(GoodSelectors.goods))),
        transformer,
      ),
      this.store$.select(additionalFieldsWithStrings).pipe(first()),
    ]);

    return combineLatest([queries, this.translateService.onLangChange.pipe(startWith(null))]).pipe(
      map(([[couriers, statuses, senders, goods, fields]]) => {
        const additions = fields.reduce((result, field) => {
          const number = defaultAdditionalFieldNumber(field);
          const name = number === undefined
            ? field.label
            : this.translateService.instant(
                'GROUPERS.additional_field',
                { number },
              );
          return { ...result, [`additional.${field.name}`]: { name, type: 'string' } };
        }, {});
        return {
          'comment': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.comment'),
            type: 'string',
          },
          'shippingCost': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.shipping_cost'),
            type: 'number',
          },
          'lgStandingData.outProjectId': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.project_id'),
            type: 'number',
          },
          'lgStandingData.outOrderId': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.order_id'),
            type: 'number',
          },
          'cart.goods[*].good.id': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.goods'),
            type: 'entity',
            operators: ['=', 'in', 'not in'],
            options: goods,
          },
          'cart.goods[*].good.name': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.product_name'),
            type: 'string',
          },
          'cart.goods[*].good.alias': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.product_sku'),
            type: 'string',
          },
          'cart.totalQuantity': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.goods_quantity'),
            type: 'string',
          },
          'cart.goods[*].quantity': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.goods_quantity_deprecated'),
            type: 'string',
          },
          'recipient.phone': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.phone'),
            type: 'string',
            operators: ['=', 'in', 'not in'],
          },
          'recipient.postcode': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.recipient_postcode'),
            type: 'string',
          },
          'recipient.region': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.recipient_region'),
            type: 'string',
          },
          'recipient.city': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.recipient_city'),
            type: 'string',
          },
          'recipient.address': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.recipient_address'),
            type: 'string',
          },
          'status.id': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.status'),
            type: 'entity',
            operators: ['=', 'in', 'not in'],
            options: statuses,
          },
          'status.name': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.status_name'),
            type: 'string',
            operators: ['=', 'in', 'not in'],
          },
          'logistic.courier.id': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.couriers'),
            type: 'entity',
            operators: ['=', 'in', 'not in'],
            options: couriers,
          },
          'logistic.courier.name': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.courier_name'),
            type: 'entity',
            operators: ['=', 'in', 'not in'],
            options: couriers,
          },
          'logistic.sender.id': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.sender'),
            type: 'entity',
            operators: ['=', 'in', 'not in'],
            options: senders,
          },
          'createdAt': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.created_at'),
            type: 'datetime',
            operators: ['=', '!=', '>', '>=', '<', '<='],
          },
          'updatedAt': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.updated_at'),
            type: 'datetime',
            operators: ['=', '!=', '>', '>=', '<', '<='],
          },
          'logistic.createdAt': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.exported_at'),
            type: 'datetime',
            operators: ['=', '!=', '>', '>=', '<', '<='],
          },
          'boughtOutAt': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.bought_out_at'),
            type: 'datetime',
            operators: ['=', '!=', '>', '>=', '<', '<='],
          },
          'returnedAt': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.returned_at'),
            type: 'datetime',
            operators: ['=', '!=', '>', '>=', '<', '<='],
          },
          'statusChangeAt': {
            name: this.translateService.instant('QUERY_BUILDER.FIELDS.status_changed_at'),
            type: 'datetime',
            operators: ['=', '!=', '>', '>=', '<', '<='],
          },
          ...additions,
        };
      }),
      distinctUntilChanged(isEqual),
    );
  }
}
